<template>
  <section v-if="phases.length">
    <div class="mb-3 flex flex-row items-center gap-2">
      <h2 class="text-headline-medium grow">
        {{ $t('lumia.sections.phases') }}
      </h2>

      <div class="flex flex-row items-center gap-1">
        <span class="text-body-medium text-on-surface-variant">{{
          t('lumia.phaseCount', { count: phases.length })
        }}</span>
        ・
        <span class="text-body-medium text-on-surface-variant">{{
          t('lumia.durationMinutes', { minutes: totalDuration })
        }}</span>
      </div>
    </div>
    <div class="flex flex-row gap-0.5">
      <a
        v-for="phase in phases"
        v-tooltip="$t(`lumia.phase.types.${phase.type}`)"
        :key="phase.phaseId"
        :href="`#${phase.phaseId}`"
        :style="{ width: percentageTimeForPhase(phase.durationMinutes) + '%' }"
        class="bg-primary-container rounded-small first:rounded-l-large last:rounded-r-large text-body-small flex h-10 items-center gap-2 overflow-hidden px-2.5 whitespace-nowrap"
      >
        <span class="text-on-primary-container shrink grow truncate">{{
          $t(`lumia.phase.types.${phase.type}`)
        }}</span>
        <span class="shrink-0">{{ phase.durationMinutes }}′</span>
      </a>
    </div>
    <div class="mt-6 flex flex-col items-center gap-8">
      <LessonImpulsePhaseBlock
        v-for="phase in phases"
        :key="phase.phaseId"
        :phase="phase"
        :mission="mission"
        :reflexion-questions="reflexionQuestions"
        :indicators="indicators"
        :lesson-feedback="lessonFeedback"
        :materials="materialsForPhase(materials, phase.phaseId)"
        :start-time="startTimeForPhase(phase.phaseId)"
      />
    </div>
  </section>
</template>
<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import type { Member } from '@/core/models/member/member'
  import type { LessonTeam } from '../models/lesson'
  import {
    materialsForPhase,
    type Indikator,
    type LessonImpulseMaterial,
    type LessonImpulsePhase,
  } from '../models/lesson-impulse'
  import LessonImpulsePhaseBlock from './LessonImpulsePhaseBlock.vue'
  import { computed } from 'vue'

  interface Props {
    phases: ReadonlyArray<LessonImpulsePhase>
    mission: string | null
    reflexionQuestions: ReadonlyArray<{ id: string; text: string }>
    materials: ReadonlyArray<LessonImpulseMaterial>
    indicators: ReadonlyArray<Indikator>
    lessonFeedback?: {
      readonly running: boolean
      readonly teams: ReadonlyArray<LessonTeam>
      readonly members: ReadonlyArray<Member>
    }
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const startTimeForPhase = (phaseId: string): number => {
    const phaseIndex = props.phases.findIndex((phase) => phase.phaseId === phaseId)
    return props.phases
      .slice(0, Math.max(phaseIndex, 0))
      .reduce((total, phase) => total + phase.durationMinutes, 0)
  }

  const totalDuration = computed(() => {
    return props.phases.reduce((total, phase) => total + phase.durationMinutes, 0)
  })

  const percentageTimeForPhase = (duration: number): number => {
    return (duration / totalDuration.value) * 100
  }
</script>
